import api from './api';
import type { Clinic, Rental, PaginatedResponse } from '@/types';

export interface ListClinicsParams {
  status?: string;
  page?: number;
  limit?: number;
  search?: string;
}

export interface ClinicAddressJson {
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  zip?: string;
  countryCode?: string;
}

export interface CreateClinicDto {
  name: string;
  email?: string;
  phone?: string;
  billingTerms?: string;
  taxExempt?: boolean;
  isConsignmentClinic?: boolean;
  billingAddress?: ClinicAddressJson;
  shippingAddress?: ClinicAddressJson;
}

export interface ClinicStats {
  totalRentals: number;
  activeRentals: number;
  totalRevenue: number;
  outstandingBalance: number;
}

export const clinicsApi = {
  list: async (params: ListClinicsParams = {}) => {
    const response = await api.get<PaginatedResponse<Clinic>>('/clinics', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<Clinic>(`/clinics/${id}`);
    return response.data;
  },

  create: async (dto: CreateClinicDto) => {
    const response = await api.post<Clinic>('/clinics', dto);
    return response.data;
  },

  update: async (id: string, dto: Partial<CreateClinicDto>) => {
    const response = await api.patch<Clinic>(`/clinics/${id}`, dto);
    return response.data;
  },

  getRentals: async (id: string, params: { status?: string; page?: number; limit?: number } = {}) => {
    const response = await api.get<PaginatedResponse<Rental>>(`/clinics/${id}/rentals`, { params });
    return response.data;
  },

  getStats: async (id: string) => {
    const response = await api.get<ClinicStats>(`/clinics/${id}/stats`);
    return response.data;
  },

  getPatients: async (id: string, params: { page?: number; limit?: number; search?: string } = {}) => {
    const response = await api.get<PaginatedResponse<any>>(`/clinics/${id}/patients`, { params });
    return response.data;
  },

  adminCreateOrder: async (
    clinicId: string,
    dto: {
      patientId: string;
      productId: string;
      productName: string;
      productSku: string;
      variantId?: string;
      size?: string;
      weeklyRate: number;
      surgeryDate: string;
      rentalWeeks: number;
      isBilateral?: boolean;
      bilateralPrice?: number;
      isClinicFulfil: boolean;
      shippingAddressType?: string;
      specialInstructions?: string;
      addOns?: { name: string; sku: string; price: number; quantity: number }[];
      wrap?: {
        productId: string;
        productTitle: string;
        variantId?: string;
        variantTitle?: string;
        sku?: string;
      };
    },
  ) => {
    const response = await api.post<{
      status: 'completed' | 'pending_payment';
      draftOrderId: string;
      shopifyOrderId?: string;
      shopifyOrderName?: string;
      invoiceUrl?: string;
    }>(`/clinics/${clinicId}/admin-create-order`, dto);
    return response.data;
  },
};

export default clinicsApi;
